import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Minus, Trash2, ShoppingCart } from "lucide-react";
import AppHeader from "@/components/drill/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import MobileSelect from "@/components/drill/MobileSelect";
import { loadOrder, saveOrder } from "@/lib/orderStore";

const CATEGORIES = ["Bits", "Casing", "Consumables", "Muds", "Oils", "PPE", "Rig spares", "Subs"];

export default function Order() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("Consumables");
  const [name, setName] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [uom, setUom] = useState("each");

  useEffect(() => {
    setItems(loadOrder());
  }, []);

  const update = (next) => {
    setItems(next);
    saveOrder(next);
  };

  const addItem = (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || Number(quantity) <= 0) return;
    const key = `${category}::${trimmed}`.toLowerCase();
    const next = [...items];
    const existing = next.find((item) => `${item.category}::${item.name}`.toLowerCase() === key);
    if (existing) existing.quantity = Number(existing.quantity || 0) + Number(quantity);
    else next.push({ id: globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`, category, name: trimmed, quantity: Number(quantity), uom });
    update(next);
    setName("");
    setQuantity(1);
    setUom("each");
  };

  const adjust = (id, delta) => {
    update(items.map((item) => item.id === id ? { ...item, quantity: Math.max(0, Number(item.quantity || 0) + delta) } : item));
  };

  const remove = (id) => update(items.filter((item) => item.id !== id));

  const grouped = CATEGORIES.map((cat) => ({ category: cat, items: items.filter((item) => item.category === cat) })).filter((g) => g.items.length);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      <AppHeader title="Order" subtitle="Equipment and consumables order sheet" backTo="/" />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pb-24 space-y-6">
        <section className="bg-white rounded-3xl border border-slate-200 p-6 dark:bg-slate-900 dark:border-slate-800">
          <div className="flex items-start gap-3 mb-5">
            <ShoppingCart className="w-5 h-5 text-slate-500 mt-0.5" />
            <div>
              <h2 className="font-semibold text-slate-900 dark:text-slate-100">Add item to order</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">Add or adjust items before submitting your order.</p>
            </div>
          </div>
          <form onSubmit={addItem} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_140px_auto] gap-3 items-end">
            <div><Label>Category</Label><MobileSelect value={category} onValueChange={setCategory} options={CATEGORIES.map((value) => ({ value, label: value }))} placeholder="Select category" /></div>
            <div><Label>Item</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Item name" className="rounded-xl" /></div>
            <div className="space-y-2">
              <div><Label>QTY</Label><Input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} className="rounded-xl" /></div>
              <div><Label>UOM</Label><MobileSelect value={uom} onValueChange={setUom} options={["bag","box","drum","each","kg","L","pallet"].sort((a,b)=>a.localeCompare(b)).map(v=>({value:v,label:v}))} /></div>
            </div>
            <Button type="submit" className="rounded-xl gap-2"><Plus className="w-4 h-4" /> Add</Button>
          </form>
        </section>

        <section className="bg-white rounded-3xl border border-slate-200 overflow-hidden dark:bg-slate-900 dark:border-slate-800">
          <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
            <div><h2 className="font-semibold">Order sheet</h2><p className="text-sm text-slate-500">{items.length} item{items.length === 1 ? "" : "s"}</p></div>
          </div>
          {grouped.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">No items in the order yet.</div>
          ) : grouped.map((group) => (
            <div key={group.category} className="border-b last:border-b-0 border-slate-200 dark:border-slate-800">
              <div className="px-6 py-3 bg-slate-50 dark:bg-slate-800/50 font-semibold text-sm">{group.category}</div>
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {group.items.map((item) => (
                  <div key={item.id} className="px-6 py-4 flex items-center gap-3">
                    <div className="min-w-0 flex-1"><div className="font-medium truncate">{item.name}</div><div className="text-xs text-slate-400">{item.uom || "each"}</div>{item.source && <div className="text-xs text-slate-400">{item.source}</div>}</div>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="icon" className="h-10 w-10 rounded-xl" onClick={() => adjust(item.id, -1)}><Minus className="w-4 h-4" /></Button>
                      <span className="w-10 text-center font-semibold tabular-nums">{item.quantity}</span>
                      <Button variant="outline" size="icon" className="h-10 w-10 rounded-xl" onClick={() => adjust(item.id, 1)}><Plus className="w-4 h-4" /></Button>
                    </div>
                    <Button variant="ghost" size="icon" className="h-10 w-10 rounded-xl text-red-500" onClick={() => remove(item.id)} aria-label={`Remove ${item.name}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>
        <Button variant="outline" className="rounded-xl" onClick={() => navigate("/")}>Back to Home</Button>
      </main>
    </div>
  );
}
